import {
  useTranslations as useNextIntlTranslations,
  useLocale,
} from 'next-intl';

export function useTranslations() {
  const t = useNextIntlTranslations('input');
  const tPoliticsAndTerms = useNextIntlTranslations('politicsAndTerms');
  const tSecurity = useNextIntlTranslations('security');
  const tContract = useNextIntlTranslations('contract');
  const tButtons = useNextIntlTranslations('buttons');
  const tPages = useNextIntlTranslations('pages');
  const tSeller = useNextIntlTranslations('seller');
  const locale = useLocale();

  const getPlaceholder = (key: string) => t(`placeholder.${key}`);
  const getLabel = (key: string) => t(`label.${key}`);
  const getError = (key: string) => t(`error.${key}`);
  const getPoliticsAndTermsText = (key: string) => tPoliticsAndTerms(key);
  const getSecurityText = (key: string) => tSecurity(key);

  const getDocumentType = () => {
    const documentType = {
      'es-PE': 'dni',
      'pt-BR': 'cpf',
    };

    return documentType[locale] || 'cpf';
  };

  const getDocumentLabel = () => {
    return locale === 'es-PE' ? getLabel('dni') : getLabel('document');
  };

  const getDocumentPlaceholder = () => {
    return locale === 'es-PE'
      ? getPlaceholder('dni')
      : getPlaceholder('document');
  };

  const getResponsibleDocumentLabel = () => {
    return locale === 'es-PE'
      ? getLabel('responsibleDni')
      : getLabel('responsibleDocument');
  };

  const getResponsibleDocumentPlaceholder = () => {
    return locale === 'es-PE'
      ? getPlaceholder('responsibleDni')
      : getPlaceholder('responsibleDocument');
  };

  const getValidationError = (type: string) => {
    switch (type.toUpperCase()) {
      case 'NAME':
        return {
          tooLong: t('error.invalidNameLength'),
          format: t('error.invalidNameFormat'),
          incomplete: t('error.invalidNameComplete'),
        };
      case 'EMAIL':
        return {
          format: t('error.invalidEmailFormat'),
        };
      case 'PHONE':
        return {
          format: t('error.invalidPhone'),
          formatEM: t('error.invalidPhoneEM'),
        };
      case 'CARD_NUMBER':
        return {
          format: t('error.invalidCardNumberFormat'),
        };
      case 'CARD_EXPIRE_DATE':
        return {
          format: t('error.invalidCardExpiryFormat'),
        };
      case 'CARD_CVV':
        return {
          format: t('error.invalidCardCVV'),
        };
      case 'REGISTRY':
        return {
          format: t('error.invalidRegistry'),
        };
      case 'CPF':
        return {
          format: t('error.invalidCPF'),
        };
      case 'CNPJ':
        return {
          format: t('error.invalidCNPJ'),
        };
      case 'DNI':
        return {
          format: t('error.invalidDNI'),
        };
      case 'ADDRESS':
        return {
          format: t('error.invalidAddress'),
        };
      case 'BAIRRO':
        return {
          format: t('error.invalidDistrict'),
        };
      case 'ESTADO':
        return {
          format: t('error.invalidState'),
        };
      case 'CIDADE':
        return {
          format: t('error.invalidCity'),
        };
      case 'PAIS':
        return {
          format: t('error.invalidCountry'),
        };
      case 'CEP':
        return {
          format: t('error.invalidZipCode'),
        };
      case 'NUMERO':
        return {
          format: t('error.invalidNumber'),
        };
      case 'COMPLEMENTO':
        return {
          format: t('error.invalidComplement'),
        };
      case 'BIRTHDATE':
        return {
          format: t('error.invalidBirthDate'),
        };
      case 'POLITICS':
        return {
          format: t('error.politicsAndTermsRequired'),
        };
      default:
        return {
          format: t('error.required'),
        };
    }
  };

  return {
    t,
    tPoliticsAndTerms,
    tContract,
    tButtons,
    tPages,
    tSeller,
    locale,
    getPlaceholder,
    getLabel,
    getError,
    getPoliticsAndTermsText,
    getSecurityText,
    getDocumentType,
    getDocumentLabel,
    getDocumentPlaceholder,
    getResponsibleDocumentLabel,
    getResponsibleDocumentPlaceholder,
    getValidationError,
  };
}
